import { useEffect, useState, useCallback } from 'react';
import { Plus, X, Save, AlertTriangle, Key, Trash2, Edit, PanelLeft, Mail } from 'lucide-react';
import { api } from '../../../api/client';
import type { ClientUser } from '../../../types/domain';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { loadSession } from '../../../auth/auth';
import { NAV_CATALOG, NAV_GROUPS } from '../nav-catalog';

/** Meni naloga odredjuju admin i prodavac (ADR-051) - isto pravilo kao na serveru. */
const canEditNav = () => ['admin', 'reseller'].includes(loadSession()?.admin.role ?? '');

export function UsersTab({ tenantId }: { tenantId: string }) {
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [resettingFor, setResettingFor] = useState<ClientUser | null>(null);
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClientUser | null>(null);
  const [navFor, setNavFor] = useState<ClientUser | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  /** Link za lozinku mejlom - panel ne mora da zna ni da saopštava tuđu lozinku. */
  const posaljiPozivnicu = async (u: ClientUser) => {
    setError(null); setInfo(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/users/${u.id}/invite`, {});
      setInfo(`Link za postavljanje lozinke je poslat na ${u.email}.`);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Slanje nije uspelo');
    }
  };

  const refresh = useCallback(async () => {
    try {
      setUsers(await api.get<ClientUser[]>(`/admin/tenants/${tenantId}/users`));
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [tenantId]);

  useEffect(() => { refresh(); }, [refresh]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.del(`/admin/tenants/${tenantId}/users/${deleteTarget.id}`);
    refresh();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--color-text-strong)', fontWeight: 600 }}>POS klijent korisnici ({users.length})</h3>
        <div style={{ flex: 1 }} />
        <button onClick={() => setCreating(true)} style={btnPrimary}><Plus size={14} /> Novi korisnik</button>
      </div>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {info && <div style={{ ...errorBox, background: "rgba(34,197,94,0.12)", color: "#86efac" }}>{info}</div>}

      <div style={card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%' }}>
            <thead><tr style={{ background: 'var(--color-bg)' }}>
              <th style={th}>Username</th><th style={th}>Email</th><th style={th}>Ime</th><th style={th}>Role</th><th style={th}>Status</th><th style={th}>Meni</th><th style={th}>Posl. login</th>
              <th style={{ ...th, textAlign: 'right' }}>Akcije</th>
            </tr></thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>Nema korisnika</td></tr>}
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{u.username}</td>
                  <td style={td}>{u.email || '-'}</td>
                  <td style={td}>{u.fullName}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{u.role}</td>
                  <td style={td}>{u.isActive ? <span style={{ color: '#10b981' }}>Aktivan</span> : <span style={{ color: '#fca5a5' }}>Neaktivan</span>}</td>
                  <td style={{ ...td, fontSize: 12, color: u.navKeys?.length ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                    {u.navKeys?.length ? `${u.navKeys.length} stavki` : 'Sve po ulozi'}
                  </td>
                  <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 11 }}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('sr-Latn-RS') : '-'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => setEditingUser(u)} style={iconBtn} title="Izmeni"><Edit size={13} /></button>
                    {canEditNav() && <button onClick={() => setNavFor(u)} style={iconBtn} title="Šta vidi u meniju"><PanelLeft size={13} /></button>}
                    <button onClick={() => void posaljiPozivnicu(u)} style={iconBtn} title={u.email ? 'Pošalji pozivnicu / link za lozinku mejlom' : 'Korisnik nema email'} disabled={!u.email}><Mail size={13} /></button>
                    <button onClick={() => setResettingFor(u)} style={iconBtn} title="Reset password"><Key size={13} /></button>
                    <button onClick={() => setDeleteTarget(u)} style={{ ...iconBtn, color: '#fca5a5' }} title="Deaktiviraj"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <UserCreateDialog tenantId={tenantId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
      {editingUser && <UserEditDialog tenantId={tenantId} user={editingUser} onClose={() => setEditingUser(null)} onSaved={() => { setEditingUser(null); refresh(); }} />}
      {navFor && <NavKeysDialog tenantId={tenantId} user={navFor} onClose={() => setNavFor(null)} onSaved={() => { setNavFor(null); refresh(); }} />}
      {resettingFor && <PasswordResetDialog tenantId={tenantId} user={resettingFor} onClose={() => setResettingFor(null)} onSaved={() => setResettingFor(null)} />}
      {deleteTarget && (
        <ConfirmDialog
          title="Deaktiviraj korisnika"
          message={`Da li želite da deaktivirate korisnika "${deleteTarget.username}" (${deleteTarget.fullName})? Više neće moći da se loguje na POS sistem.`}
          confirmLabel="Deaktiviraj"
          variant="danger"
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function UserCreateDialog({ tenantId, onClose, onSaved }: { tenantId: string; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(false);
  const [pozivnica, setPozivnica] = useState(false);
  const [role, setRole] = useState('admin');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/users`, pozivnica
        ? { username, email, fullName, role, sendInvite: true }
        : { username, email, fullName, password, mustChangePassword: mustChange, role });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Novi POS korisnik</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Username *"><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required style={input} /></Field>
          <Field label="Email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={input} /></Field>
          <Field label="Puno ime *"><input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required style={input} /></Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={pozivnica} onChange={(e) => setPozivnica(e.target.checked)} />
            Pošalji pozivnicu mejlom - korisnik sam postavlja lozinku
          </label>
          {!pozivnica && <Field label="Lozinka *"><input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={input} /></Field>}
          {pozivnica && !email && <div style={{ fontSize: 12, color: '#fbbf24' }}>Za pozivnicu upišite email korisnika.</div>}
          <Field label="Role *">
            <select value={role} onChange={(e) => setRole(e.target.value)} style={input}>
              <option value="admin">Admin (vlasnik biznisa)</option>
              <option value="manager">Manager</option>
              <option value="operator">Operator (kasir)</option>
            </select>
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} />
            Mora promeniti lozinku pri sledećem login-u
          </label>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}><Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordResetDialog({ tenantId, user, onClose, onSaved }: { tenantId: string; user: ClientUser; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/users/${user.id}/reset-password`, { password, mustChange });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Reset lozinke: {user.username}</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Nova lozinka *"><input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={input} /></Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} />
            Mora promeniti pri sledećem login-u
          </label>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>{saving ? 'Čuvanje...' : 'Sačuvaj'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserEditDialog({ tenantId, user, onClose, onSaved }: { tenantId: string; user: ClientUser; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const [fullName, setFullName] = useState(user.fullName);
  const [email, setEmail] = useState(user.email || '');
  const [role, setRole] = useState(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.put(`/admin/tenants/${tenantId}/users/${user.id}`, {
        fullName: fullName.trim(),
        email: email.trim() || null,
        role,
        isActive,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Izmena: {user.username}</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Username (ne može se menjati)">
            <input type="text" value={user.username} disabled style={{ ...input, opacity: 0.5 }} />
          </Field>
          <Field label="Puno ime *">
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required style={input} autoFocus />
          </Field>
          <Field label="Email">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={input} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Role">
              <select value={role} onChange={(e) => setRole(e.target.value)} style={input}>
                <option value="admin">Admin (vlasnik biznisa)</option>
                <option value="manager">Manager</option>
                <option value="operator">Operator (kasir)</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')} style={input}>
                <option value="active">Aktivan</option>
                <option value="inactive">Neaktivan</option>
              </select>
            </Field>
          </div>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving || !fullName.trim()} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Sta nalog vidi u bocnom meniju (ADR-051).
 *
 * Lista samo SUZAVA: operater i dalje ne vidi ono sto mu uloga ne dozvoljava,
 * ma sta bilo stiklirano ovde. „Sve po ulozi" cuva praznu listu - tako nova
 * stavka menija, dodata posle, ne ostaje skrivena nalozima koji nisu ni bili
 * ograniceni.
 */
function NavKeysDialog({ tenantId, user, onClose, onSaved }: { tenantId: string; user: ClientUser; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const initial = user.navKeys ?? [];
  const [restricted, setRestricted] = useState(initial.length > 0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial.length > 0 ? initial : NAV_CATALOG.map((i) => i.key)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const setGroup = (group: string, on: boolean) => setSelected((prev) => {
    const next = new Set(prev);
    for (const i of NAV_CATALOG) if (i.group === group) { if (on) next.add(i.key); else next.delete(i.key); }
    return next;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (restricted && selected.size === 0) { setError('Izaberite bar jednu stavku, ili ostavite „Sve po ulozi".'); return; }
    setSaving(true);
    setError(null);
    try {
      // Redosled kataloga, ne redosled klikova - lista je citljiva u auditu.
      const navKeys = restricted ? NAV_CATALOG.map((i) => i.key).filter((k) => selected.has(k)) : [];
      await api.put(`/admin/tenants/${tenantId}/users/${user.id}`, { navKeys });
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...dialog, maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Meni: {user.username}</h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            Važi za sve operatere koji rade pod ovim nalogom. Lista samo sužava: ono što uloga operatera
            ne vidi, neće videti ni ako je ovde uključeno. Pravni dokumenti su uvek dostupni.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="radio" checked={!restricted} onChange={() => setRestricted(false)} />
            Sve po ulozi (bez ograničenja)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="radio" checked={restricted} onChange={() => setRestricted(true)} />
            Samo izabrane stavke ({selected.size} od {NAV_CATALOG.length})
          </label>

          {restricted && NAV_GROUPS.map((group) => {
            const items = NAV_CATALOG.filter((i) => i.group === group);
            const allOn = items.every((i) => selected.has(i.key));
            return (
              <fieldset key={group} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 14px', margin: 0 }}>
                <legend style={{ padding: '0 6px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={allOn} onChange={() => setGroup(group, !allOn)} />
                    {group}
                  </label>
                </legend>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 6 }}>
                  {items.map((i) => (
                    <label key={i.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected.has(i.key)} onChange={() => toggle(i.key)} />
                      {i.label}
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>{label}</label>{children}</div>;
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 480, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-strong)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
